import type { SearchCollectionKey } from '@shared/constants/searchCollections';
import { getSectionConfig } from '../constants/sections';
import { type Filter, filterId } from './filters';

/**
 * Span-based token parsing for the ⌘K palette.
 *
 * The raw query STRING is the single source of truth. A registered `<token>:` (or
 * an `is:open` / `is:closed` modifier) is recognised ANYWHERE in the string as a
 * whitespace-delimited word and RETAINED in the text — it is not stripped. Each
 * recognised token is reported as a `{ start, end, filter }` span so the overlay
 * can paint it as a pill and the search layer can derive its filter. The remaining
 * non-token text (including the residual value of a `lot:romaine`-style token) is
 * the search term.
 *
 * A type token scopes only when it maps to a real section whose collection the
 * caller is ENTITLED to; anything else stays plain text (an unentitled or unknown
 * token is a literal, never a pill and never a filter — a pill would imply it is
 * active). `is:open`/`is:closed` compose with any number of type tokens.
 *
 *   parseQuerySpans('so: watermelon',   ['orders'])  ─▶ span[0..3] type:sales-orders · term 'watermelon'
 *   parseQuerySpans('lot:romaine po:x',  ['lots','orders']) ─▶ spans lot:/po: · term 'romaine x'
 *   parseQuerySpans('is:open romaine',   ['orders'])  ─▶ span is:open lifecycle · term 'romaine'
 *   parseQuerySpans('ABC:123',           [...])       ─▶ no span · term 'ABC:123'    (unregistered)
 *   parseQuerySpans('so:x',              ['bills'])    ─▶ no span · term 'so:x'       (unentitled)
 */

/** Canonical singular token shown to the user for each collection (the zero-results tip examples). */
export const SCOPE_TOKEN_BY_COLLECTION: Record<SearchCollectionKey, string> = {
  orders: 'order',
  products: 'product',
  lots: 'lot',
  productionRuns: 'run',
  bills: 'bill',
  payments: 'payment',
  inventoryTransfers: 'transfer',
  receivingTickets: 'receiving',
  pickTickets: 'pick',
  ledgerAccounts: 'account',
};

/**
 * Every accepted alias → section key. Per-section acronyms
 * (`so`/`po`/`gp`/`bso`/`inv`/`pyb`/…) match `web/src/utils/entityTypes.ts` and
 * the page titles (RT/PT). `order:`/`bill:` are UMBRELLA scopes (whole
 * collection); `invoice:`/`payable:` resolve to their sub-section, not the whole
 * `bills`.
 */
const SCOPE_ALIASES: Record<string, string> = {
  // Umbrella scopes — the whole collection, no discriminator.
  order: 'orders',
  orders: 'orders',
  bill: 'bills',
  bills: 'bills',
  // Order sections.
  so: 'sales-orders',
  salesorder: 'sales-orders',
  salesorders: 'sales-orders',
  po: 'purchase-orders',
  purchaseorder: 'purchase-orders',
  purchaseorders: 'purchase-orders',
  gp: 'grower-products',
  wo: 'grower-products',
  workorder: 'grower-products',
  workorders: 'grower-products',
  growerproduct: 'grower-products',
  growerproducts: 'grower-products',
  bso: 'buy-sell',
  buysell: 'buy-sell',
  buysellorder: 'buy-sell',
  buysellorders: 'buy-sell',
  // Bill sections — `invoice:`/`payable:` narrowed to their sub-section.
  inv: 'invoices',
  invoice: 'invoices',
  invoices: 'invoices',
  pyb: 'payables',
  payable: 'payables',
  payables: 'payables',
  // Everything else stays 1:1.
  product: 'products',
  products: 'products',
  lot: 'lots',
  lots: 'lots',
  be: 'business-entities',
  entity: 'business-entities',
  entities: 'business-entities',
  businessentity: 'business-entities',
  businessentities: 'business-entities',
  pr: 'production-runs',
  run: 'production-runs',
  runs: 'production-runs',
  production: 'production-runs',
  productionrun: 'production-runs',
  productionruns: 'production-runs',
  pymt: 'payments',
  payment: 'payments',
  payments: 'payments',
  it: 'inventory-transfers',
  transfer: 'inventory-transfers',
  transfers: 'inventory-transfers',
  inventorytransfer: 'inventory-transfers',
  inventorytransfers: 'inventory-transfers',
  rt: 'receiving-tickets',
  receiving: 'receiving-tickets',
  receivingticket: 'receiving-tickets',
  receivingtickets: 'receiving-tickets',
  receipt: 'receiving-tickets',
  receipts: 'receiving-tickets',
  pt: 'pick-tickets',
  pick: 'pick-tickets',
  picks: 'pick-tickets',
  pickticket: 'pick-tickets',
  picktickets: 'pick-tickets',
  acct: 'ledger-accounts',
  gl: 'ledger-accounts',
  account: 'ledger-accounts',
  accounts: 'ledger-accounts',
  ledger: 'ledger-accounts',
  ledgeraccount: 'ledger-accounts',
  ledgeraccounts: 'ledger-accounts',
};

/** The canonical token a facet tap inserts for each section key — the reverse of `SCOPE_ALIASES`, one short form per section. */
export const SECTION_SCOPE_TOKEN: Record<string, string> = {
  orders: 'order',
  'sales-orders': 'so',
  'purchase-orders': 'po',
  'grower-products': 'gp',
  'buy-sell': 'bso',
  bills: 'bill',
  invoices: 'inv',
  payables: 'pyb',
  products: 'product',
  lots: 'lot',
  'business-entities': 'be',
  'production-runs': 'pr',
  payments: 'pymt',
  'inventory-transfers': 'it',
  'receiving-tickets': 'rt',
  'pick-tickets': 'pt',
  'ledger-accounts': 'acct',
};

// A whole word that is exactly `is:open` / `is:closed` (case-insensitive) — the
// lifecycle modifier. Whole-word so `is:openish` is not mistaken for `is:open`.
const LIFECYCLE_WORD = /^is:(open|closed)$/i;
// A leading `letters:` on a word — letters only before the colon (so `SO-5:` /
// `http://` never scope). The rest of the word is the token's residual value.
const TYPE_PREFIX = /^([a-zA-Z]+):/;

// Which entitled collection to showcase in the tip, most useful first, so the
// example is always one the user can actually type (never advertises a 403 scope).
const EXAMPLE_PRIORITY: readonly SearchCollectionKey[] = [
  'lots',
  'orders',
  'bills',
  'products',
  'payments',
  'receivingTickets',
  'pickTickets',
  'inventoryTransfers',
  'productionRuns',
  'ledgerAccounts',
];

/** A recognised token's position in the raw string and the filter it derives. */
export interface TokenSpan {
  /** Index of the first token character in the raw query. */
  start: number;
  /** Index one past the token (the colon, for a type token; the value, for `is:`). */
  end: number;
  /** The breadth filter this token contributes (type or lifecycle, never status). */
  filter: Extract<Filter, { dim: 'type' } | { dim: 'lifecycle' }>;
}

export interface QueryParse {
  /** Recognised token spans, in order of appearance. */
  spans: TokenSpan[];
  /** Derived breadth filters (type + lifecycle), deduped by id. */
  filters: Filter[];
  /** The search term: the raw string with every token span removed, whitespace-collapsed and trimmed. */
  term: string;
}

/** Classify one whitespace-delimited word into a token span, or null for plain text. */
function classifyWord(word: string, wordStart: number, entitledCollections: readonly string[]): TokenSpan | null {
  const lifecycle = LIFECYCLE_WORD.exec(word);
  if (lifecycle) {
    return {
      start: wordStart,
      end: wordStart + word.length,
      filter: { dim: 'lifecycle', open: lifecycle[1].toLowerCase() === 'open' },
    };
  }

  const typed = TYPE_PREFIX.exec(word);
  if (typed) {
    const sectionKey = SCOPE_ALIASES[typed[1].toLowerCase()];
    const config = sectionKey ? getSectionConfig(sectionKey) : undefined;
    if (config && entitledCollections.includes(config.collection)) {
      // The span is just the `alias:` prefix; any residual after the colon
      // (`lot:romaine` → `romaine`) stays as search text.
      return { start: wordStart, end: wordStart + typed[1].length + 1, filter: { dim: 'type', section: sectionKey } };
    }
  }

  return null;
}

/** The chip boundary marker (U+200B) is treated as a word separator and NEVER survives into the search term — the `q` sent to Typesense is always marker-free. */
function stripSpans(query: string, spans: readonly TokenSpan[]): string {
  let out = query;
  // Right-to-left so earlier indices stay valid as we splice.
  for (const span of [...spans].sort((a, b) => b.start - a.start)) {
    out = out.slice(0, span.start) + out.slice(span.end);
  }
  return out
    .replace(/\u200b/g, ' ')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

/**
 * Parse the raw query into token spans, the derived breadth filters, and the bare
 * search term. Tokens are recognised anywhere and RETAINED; degrades to plain text
 * for an unknown token or one whose collection the caller's key does not grant.
 */
export function parseQuerySpans(query: string, entitledCollections: readonly string[]): QueryParse {
  const spans: TokenSpan[] = [];
  // A word is a maximal run of non-whitespace, non-boundary-marker characters: the
  // marker (U+200B) is a hard separator so a token typed after an escape is recognised.
  const word = /[^\s\u200b]+/g;
  let match: RegExpExecArray | null = word.exec(query);
  while (match !== null) {
    const span = classifyWord(match[0], match.index, entitledCollections);
    if (span) spans.push(span);
    match = word.exec(query);
  }

  const seen = new Set<string>();
  const filters: Filter[] = [];
  for (const span of spans) {
    const id = filterId(span.filter);
    if (!seen.has(id)) {
      seen.add(id);
      filters.push(span.filter);
    }
  }

  return { spans, filters, term: stripSpans(query, spans) };
}

/**
 * Prepend `sectionKey`'s type token to the query, separated by the U+200B boundary
 * marker so the pill paints as its own chip. No-op when the section has no token.
 */
export function prependTypeToken(query: string, sectionKey: string): string {
  const token = SECTION_SCOPE_TOKEN[sectionKey];
  if (!token) return query;
  return query.length === 0 ? `${token}:` : `${token}:\u200b${query}`;
}

/** Strip every type token from the query (the "All" filter), keeping lifecycle tokens and text. */
export function removeAllTypeTokens(query: string, entitledCollections: readonly string[]): string {
  const { spans } = parseQuerySpans(query, entitledCollections);
  return stripSpans(
    query,
    spans.filter((span) => span.filter.dim === 'type')
  );
}

/**
 * Strip the type token(s) for ONE section from the query (a filter tap on an
 * already-active chip — toggle off), keeping every other token and the term text.
 * A section repeated as two pills has both spans removed.
 */
export function removeTypeToken(query: string, sectionKey: string, entitledCollections: readonly string[]): string {
  const { spans } = parseQuerySpans(query, entitledCollections);
  return stripSpans(
    query,
    spans.filter((span) => span.filter.dim === 'type' && span.filter.section === sectionKey)
  );
}

/** Up to `limit` example scope tokens (`lot:`, `order:`) drawn from the entitled collections, for the zero-results tip. */
export function scopeExamples(entitledCollections: readonly string[], limit = 2): string[] {
  const entitled = new Set(entitledCollections);
  const examples: string[] = [];
  for (const collection of EXAMPLE_PRIORITY) {
    if (entitled.has(collection)) examples.push(`${SCOPE_TOKEN_BY_COLLECTION[collection]}:`);
    if (examples.length >= limit) break;
  }
  return examples;
}
